/**
 * GuardianshipCard — card da lista B6. Renderiza SOMENTE campos reais do
 * serializer (R15): type, wardId/guardianId (IDs opacos), scope resumido,
 * basis, datas, documentReferenceId (DocumentationGated) e status. NÃO fabrica
 * nome de pessoa / CPF / relationship (esses não existem na API).
 */

import {
  GUARDIANSHIP_BASIS_LABEL,
  GUARDIANSHIP_STATUS_LABEL,
  GUARDIANSHIP_TYPE_LABEL,
  isExpiringSoon,
  summarizeScope,
  type GuardianshipWireItem,
} from './types';

interface GuardianshipCardProps {
  item: GuardianshipWireItem;
  onOpen: (id: string) => void;
}

function formatDate(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function GuardianshipCard({ item, onOpen }: GuardianshipCardProps): JSX.Element {
  const expiring = isExpiringSoon(item);
  const scopeSummary = summarizeScope(item.scope);

  return (
    <button
      type="button"
      className={`g-card${expiring ? ' g-card--expiring' : ''}`}
      onClick={() => onOpen(item.id)}
    >
      <div className="g-card__head">
        <span className="g-card__type">{GUARDIANSHIP_TYPE_LABEL[item.type].label}</span>
        <span className={`g-status g-status--${item.status}`}>
          {GUARDIANSHIP_STATUS_LABEL[item.status]}
        </span>
      </div>

      <div className="g-card__grid">
        <span className="g-card__key">ward</span>
        <span className="g-card__val g-card__val--mono">{item.wardId}</span>
        <span className="g-card__key">guardian</span>
        <span className="g-card__val g-card__val--mono">{item.guardianId}</span>
        <span className="g-card__key">base legal</span>
        <span className="g-card__val">
          {item.basis !== null ? GUARDIANSHIP_BASIS_LABEL[item.basis] : '—'}
        </span>
        <span className="g-card__key">escopo</span>
        <span className="g-card__val">{scopeSummary ?? 'Não especificado'}</span>
        <span className="g-card__key">desde</span>
        <span className="g-card__val">{formatDate(item.effectiveFrom)}</span>
        <span className="g-card__key">até</span>
        <span className="g-card__val">
          {formatDate(item.effectiveUntil)}
          {expiring && <span className="g-card__expiring-tag"> · expira em breve</span>}
        </span>
      </div>

      <div className="g-card__foot">
        {item.documentReferenceId !== null ? (
          <span className="g-card__doc g-card__doc--ok">
            ✓ DocumentationGated · {item.documentReferenceId}
          </span>
        ) : (
          <span className="g-card__doc g-card__doc--warn">⚠ sem documentação anexada</span>
        )}
        <span className="g-card__cta">Detalhes</span>
      </div>
    </button>
  );
}
